import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import ProductOfBrand from '../components/shared/ProductOfBrand';
import DemoRequestDialog from '../components/DemoRequestDialog';
import { FREE_SIGNUP_ENABLED } from '../constants/freeSignup';

const Register = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    const result = await register(firstName, lastName, company, email, password);
    
    if (result.success) {
      setConfirmationDialogOpen(true);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img 
                src="/shield_logo.png" 
                alt="Diligence Desk Shield Logo" 
                style={{ 
                  height: '40px', 
                  width: 'auto' 
                }}
              />
              <Typography component="h1" variant="h4">
                Diligence Desk
              </Typography>
            </Box>
            <ProductOfBrand sx={{ mt: 0.75 }} />
          </Box>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            {FREE_SIGNUP_ENABLED ? 'Create your account' : 'Signup temporarily unavailable'}
          </Typography>

          {!FREE_SIGNUP_ENABLED && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Online free-account registration is paused. Request a demo and we will help you get access.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setDemoDialogOpen(true)}
                sx={{ mb: 2 }}
              >
                Request a Demo
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/login" variant="body2">
                  Already have an account? Sign In
                </Link>
              </Box>
              <DemoRequestDialog
                open={demoDialogOpen}
                onClose={() => setDemoDialogOpen(false)}
              />
            </Box>
          )}

          {FREE_SIGNUP_ENABLED && error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {FREE_SIGNUP_ENABLED && (
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="firstName"
              label="First Name"
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="lastName"
              label="Last Name"
              name="lastName"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              fullWidth
              id="company"
              label="Company (Optional)"
              name="company"
              autoComplete="organization"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
            
            {/* Legal Agreement Checkboxes */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    disabled={loading}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link 
                      href="/terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'underline' }}
                    >
                      Terms of Use
                    </Link>
                  </Typography>
                }
                sx={{ alignItems: 'center' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeToPrivacy}
                    onChange={(e) => setAgreeToPrivacy(e.target.checked)}
                    disabled={loading}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link 
                      href="/privacy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'underline' }}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                }
                sx={{ alignItems: 'center' }}
              />
            </Box>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !agreeToTerms || !agreeToPrivacy}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign Up'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                {"Already have an account? Sign In"}
              </Link>
            </Box>
          </Box>
          )}
        </Paper>
      </Box>

      {/* Email Confirmation Dialog */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        <DialogTitle id="confirmation-dialog-title">
          Registration Successful!
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirmation-dialog-description">
            Thank you for registering! We've sent a confirmation email to <strong>{email}</strong>. 
            Please check your email and click the confirmation link to activate your account.
            <br /><br />
            You can close this dialog and return to the login page to sign in once you've confirmed your email.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmationDialogOpen(false)} 
            color="primary"
          >
            Close
          </Button>
          <Button 
            onClick={() => {
              setConfirmationDialogOpen(false);
              navigate('/login');
            }} 
            color="primary"
            variant="contained"
          >
            Go to Sign In
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Register;
